import React from "react";
import { classMgmtService } from "services/admin/classMgmtService";
import type { ClassListItem, ClassSession } from "types/domain";
import ActionButton from "components/ui/ActionButton";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage } from "context/LanguageContext";

interface EnrollClassPanelProps {
  studentId: string;
  centerId: string | null;
  // Fired whenever the user toggles a class/session checkbox. Lets the
  // parent decide whether the right-hand panel still has anything to
  // show — when false, the modal can collapse back to its single-column
  // face-enrollment form.
  onSelectionChange?: (hasSelection: boolean) => void;
  // Fired whenever the form's validity changes so the parent can
  // disable the submit button until every selected class has a valid
  // remaining-sessions value.
  onValidityChange?: (isValid: boolean) => void;
}

export interface EnrollClassSelections {
  classIds: Set<string>;
  sessionIds: Set<string>;
  remainingSessionsByClassId: Map<string, string>;
}

const KNOWN_SESSION_STATUSES = new Set([
  "SCHEDULED",
  "ACTIVE",
  "COMPLETED",
  "CANCELLED",
  "CLOSED",
]);

const STATUS_TONE: Record<string, "info" | "success" | "neutral"> = {
  SCHEDULED: "info",
  ACTIVE: "success",
};

function formatDateTime(value: string | null, locale: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const EnrollClassPanel = React.forwardRef<
  { getSelections: () => EnrollClassSelections },
  EnrollClassPanelProps
>(({ studentId, centerId, onSelectionChange, onValidityChange }, ref) => {
  const { t, locale } = useLanguage();
  const [classes, setClasses] = React.useState<ClassListItem[]>([]);
  const [classesLoading, setClassesLoading] = React.useState(false);
  const [expandedClassId, setExpandedClassId] = React.useState<string | null>(
    null
  );
  const [sessionsByClass, setSessionsByClass] = React.useState<
    Map<string, ClassSession[]>
  >(new Map());
  const [sessionsLoading, setSessionsLoading] = React.useState<Set<string>>(
    new Set()
  );
  const [selectedClassIds, setSelectedClassIds] = React.useState<Set<string>>(
    new Set()
  );
  const [selectedSessionIds, setSelectedSessionIds] = React.useState<
    Set<string>
  >(new Set());
  const [remainingSessionsByClassId, setRemainingSessionsByClassId] =
    React.useState<Map<string, string>>(new Map());
  const [classSearch, setClassSearch] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const updateRemainingSessions = React.useCallback(
    (classId: string, value: string) => {
      setRemainingSessionsByClassId((prev) => {
        const next = new Map(prev);
        if (value.trim() === "") {
          next.delete(classId);
        } else {
          next.set(classId, value);
        }
        return next;
      });
    },
    []
  );

  React.useImperativeHandle(
    ref,
    () => ({
      getSelections: () => ({
        classIds: new Set(selectedClassIds),
        sessionIds: new Set(selectedSessionIds),
        remainingSessionsByClassId: new Map(remainingSessionsByClassId),
      }),
    }),
    [selectedClassIds, selectedSessionIds, remainingSessionsByClassId]
  );

  // Notify the parent so it can collapse the right-hand panel back into
  // single-column when the user un-selects everything.
  const isInitialMount = React.useRef(true);
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const hasSelection =
      selectedClassIds.size > 0 || selectedSessionIds.size > 0;
    onSelectionChange?.(hasSelection);
  }, [selectedClassIds, selectedSessionIds, onSelectionChange]);

  // Every selected class must have a non-negative remaining-sessions value
  // before the form can be submitted. Empty selections are always valid.
  const isFormValid = React.useMemo(() => {
    if (selectedClassIds.size === 0) return true;
    for (const cid of selectedClassIds) {
      const raw = remainingSessionsByClassId.get(cid);
      if (!raw || raw.trim() === "") return false;
      const num = Number(raw);
      if (!Number.isFinite(num) || num < 0) return false;
    }
    return true;
  }, [selectedClassIds, remainingSessionsByClassId]);

  React.useEffect(() => {
    onValidityChange?.(isFormValid);
  }, [isFormValid, onValidityChange]);

  React.useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    const load = async () => {
      setClassesLoading(true);
      setError(null);
      try {
        // Paginate to grab every class for the centre.
        const pageSize = 200;
        const collected: ClassListItem[] = [];
        let skip = 0;
        while (true) {
          const res = await classMgmtService.getAll({
            skip,
            limit: pageSize,
            center_id: centerId ?? undefined,
          });
          const items = res.items ?? [];
          collected.push(...items);
          if (items.length < pageSize || collected.length >= res.total) break;
          skip += pageSize;
        }
        if (!cancelled) setClasses(collected);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "usersMgmt.enroll.loadClassesError"
          );
        }
      } finally {
        if (!cancelled) setClassesLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [studentId, centerId]);

  const ensureSessionsForClass = React.useCallback(async (classId: string) => {
    setSessionsByClass((prev) => {
      if (prev.has(classId)) return prev;
      return prev;
    });
    setSessionsLoading((prev) => {
      if (prev.has(classId)) return prev;
      const next = new Set(prev);
      next.add(classId);
      return next;
    });
    try {
      const list = await classMgmtService.getSessions(classId);
      const upcoming = list
        .filter((s) => s.status === "SCHEDULED" || s.status === "ACTIVE")
        .sort((a, b) => {
          const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
          const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
          return ta - tb;
        });
      setSessionsByClass((prev) => {
        const next = new Map(prev);
        next.set(classId, upcoming);
        return next;
      });
    } catch {
      setSessionsByClass((prev) => {
        const next = new Map(prev);
        next.set(classId, []);
        return next;
      });
    } finally {
      setSessionsLoading((prev) => {
        const next = new Set(prev);
        next.delete(classId);
        return next;
      });
    }
  }, []);

  const toggleClass = (classId: string) => {
    setSelectedClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) {
        next.delete(classId);
        // Drop sessions under this class too so the summary stays honest.
        setSelectedSessionIds((prevSessions) => {
          const sessions = sessionsByClass.get(classId) ?? [];
          const sessionIdSet = new Set(sessions.map((s) => s.id));
          const filtered = new Set(
            Array.from(prevSessions).filter((id) => !sessionIdSet.has(id))
          );
          return filtered;
        });
      } else {
        next.add(classId);
        // Auto-expand + fetch its sessions on first selection so the user
        // immediately sees what they can also pin the student to.
        if (expandedClassId !== classId) setExpandedClassId(classId);
        if (!sessionsByClass.has(classId)) {
          void ensureSessionsForClass(classId);
        }
      }
      return next;
    });
  };

  const toggleSession = (sessionId: string) => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const filteredClasses = React.useMemo(() => {
    const q = classSearch.trim().toLowerCase();
    if (!q) return classes;
    return classes.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.class_code ?? "").toLowerCase().includes(q)
    );
  }, [classes, classSearch]);

  const expandClass = (classId: string) => {
    setExpandedClassId((prev) => (prev === classId ? null : classId));
    if (!sessionsByClass.has(classId)) {
      void ensureSessionsForClass(classId);
    }
  };

  const classNameById = React.useMemo(() => {
    const m = new Map<string, ClassListItem>();
    for (const c of classes) m.set(c.id, c);
    return m;
  }, [classes]);

  const selectedClasses = React.useMemo(
    () =>
      Array.from(selectedClassIds)
        .map((id) => classNameById.get(id))
        .filter((c): c is ClassListItem => c !== undefined),
    [selectedClassIds, classNameById]
  );

  const selectedSessions = React.useMemo(() => {
    const result: {
      classId: string;
      className: string;
      session: ClassSession;
    }[] = [];
    for (const [classId, sessions] of sessionsByClass.entries()) {
      for (const s of sessions) {
        if (selectedSessionIds.has(s.id)) {
          result.push({
            classId,
            className:
              classNameById.get(classId)?.name ??
              t("usersMgmt.enroll.classFallback"),
            session: s,
          });
        }
      }
    }
    return result;
  }, [sessionsByClass, selectedSessionIds, classNameById, t]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-line px-4 py-2">
        <h4 className="text-sm font-bold text-ink">
          {t("usersMgmt.enroll.title")}
        </h4>
        <span className="text-xs text-ink-muted">
          {t("usersMgmt.enroll.selectionCount", {
            classes: selectedClassIds.size,
            sessions: selectedSessionIds.size,
          })}
        </span>
      </div>

      {(selectedClasses.length > 0 || selectedSessions.length > 0) && (
        <div className="flex flex-wrap gap-2 border-b border-line bg-gray-50 px-4 py-2 dark:bg-canvas/40">
          {selectedClasses.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
            >
              {t("usersMgmt.enroll.classChip", { name: c.name })}
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => toggleClass(c.id)}
                aria-label={t("usersMgmt.enroll.deselectClass", {
                  name: c.name,
                })}
                className="hover:!bg-transparent !ml-1 !h-4 !min-h-0 !w-4 !min-w-0 !rounded-full !p-0 !text-brand-700 hover:!text-brand-900 dark:!text-brand-300 dark:hover:!text-brand-100"
              >
                ×
              </ActionButton>
            </span>
          ))}
          {selectedSessions.map((s) => (
            <span
              key={s.session.id}
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
            >
              {t("usersMgmt.enroll.sessionChip", {
                time: formatDateTime(s.session.start_time, locale),
                className: s.className,
              })}
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => toggleSession(s.session.id)}
                aria-label={t("usersMgmt.enroll.deselectSession", {
                  name: s.session.title ?? s.session.id,
                })}
                className="hover:!bg-transparent !ml-1 !h-4 !min-h-0 !w-4 !min-w-0 !rounded-full !p-0 !text-amber-700 hover:!text-amber-900 dark:!text-amber-300 dark:hover:!text-amber-100"
              >
                ×
              </ActionButton>
            </span>
          ))}
        </div>
      )}

      <div className="border-b border-line px-4 py-2">
        <input
          type="text"
          value={classSearch}
          onChange={(e) => setClassSearch(e.target.value)}
          placeholder={t("usersMgmt.enroll.searchPlaceholder")}
          className="w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:text-white"
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mx-4 mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300"
        >
          {t(error)}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {classesLoading ? (
          <div className="space-y-2 py-6 text-center text-xs text-ink-muted">
            <div className="border-t-transparent mx-auto h-5 w-5 animate-spin rounded-full border-2 border-brand-500" />
            <p>{t("usersMgmt.enroll.loadingClasses")}</p>
          </div>
        ) : filteredClasses.length === 0 ? (
          <div className="py-6 text-center text-xs text-ink-muted">
            {classes.length === 0
              ? t("usersMgmt.enroll.noClasses")
              : t("usersMgmt.enroll.noMatchingClasses")}
          </div>
        ) : (
          <ul className="space-y-2">
            {filteredClasses.map((c) => {
              const checked = selectedClassIds.has(c.id);
              const expanded = expandedClassId === c.id;
              const sessions = sessionsByClass.get(c.id);
              const loadingSessions = sessionsLoading.has(c.id);
              return (
                <li
                  key={c.id}
                  className="overflow-hidden rounded-lg border border-line"
                >
                  <div className="flex items-center gap-2 bg-surface px-3 py-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleClass(c.id)}
                      className="h-4 w-4 cursor-pointer accent-brand-500"
                    />
                    <button
                      type="button"
                      onClick={() => expandClass(c.id)}
                      className="flex-1 cursor-pointer text-left"
                    >
                      <p className="truncate text-sm font-semibold text-ink">
                        {c.name}
                      </p>
                      <p className="text-xs text-ink-muted">
                        {c.class_code ?? "—"}
                      </p>
                    </button>
                    <span className="shrink-0 text-xs text-ink-muted">
                      {expanded ? "▾" : "▸"}
                    </span>
                  </div>
                  {checked && (
                    <div className="flex items-center gap-2 border-t border-line bg-surface px-3 py-2">
                      <label
                        htmlFor={`remaining-sessions-${c.id}`}
                        className="text-xs font-medium text-gray-600 dark:text-gray-300"
                      >
                        {t("usersMgmt.enroll.remainingSessions")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        id={`remaining-sessions-${c.id}`}
                        type="number"
                        min={0}
                        required
                        value={remainingSessionsByClassId.get(c.id) ?? ""}
                        onChange={(e) =>
                          updateRemainingSessions(c.id, e.target.value)
                        }
                        placeholder={t(
                          "usersMgmt.enroll.remainingSessionsPlaceholder"
                        )}
                        className={`w-24 rounded-md border bg-surface px-2 py-1 text-xs outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:text-white ${ ( remainingSessionsByClassId.get(c.id) ?? ""
                          ).trim() === ""
                            ? "border-red-400 dark:border-red-500"
                            : "border-line"
                        }`}
                      />
                    </div>
                  )}
                  {expanded && (
                    <div className="border-t border-line bg-gray-50 px-3 py-2 dark:bg-canvas/40">
                      {loadingSessions ? (
                        <div className="flex items-center gap-2 py-2 text-xs text-ink-muted">
                          <div className="border-t-transparent h-3 w-3 animate-spin rounded-full border-2 border-brand-500" />
                          {t("usersMgmt.enroll.loadingSessions")}
                        </div>
                      ) : sessions === undefined ? (
                        <div className="py-2 text-xs text-ink-muted">
                          {t("usersMgmt.enroll.noSessions")}
                        </div>
                      ) : sessions.length === 0 ? (
                        <div className="py-2 text-xs text-ink-muted">
                          {t("usersMgmt.enroll.noOpenSessions")}
                        </div>
                      ) : (
                        <ul className="space-y-1">
                          {sessions.map((s) => {
                            const sessionChecked = selectedSessionIds.has(s.id);
                            return (
                              <li key={s.id}>
                                <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-white dark:hover:bg-surface-hover">
                                  <input
                                    type="checkbox"
                                    checked={sessionChecked}
                                    onChange={() => toggleSession(s.id)}
                                    disabled={!checked}
                                    className="h-3.5 w-3.5 cursor-pointer accent-brand-500 disabled:cursor-not-allowed disabled:opacity-40"
                                  />
                                  <span className="min-w-0 flex-1 truncate text-xs text-ink">
                                    {s.title ??
                                      t("usersMgmt.enroll.sessionFallback")}
                                  </span>
                                  <span className="shrink-0 text-[10px] text-ink-muted">
                                    {formatDateTime(s.start_time, locale)}
                                  </span>
                                  <StatusBadge
                                    label={
                                      KNOWN_SESSION_STATUSES.has(s.status)
                                        ? t(`enum.sessionStatus.${s.status}`)
                                        : s.status
                                    }
                                    tone={STATUS_TONE[s.status] ?? "neutral"}
                                    className="shrink-0 !px-1.5 !py-0.5 !text-[10px]"
                                  />
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
});

EnrollClassPanel.displayName = "EnrollClassPanel";

export default EnrollClassPanel;
